import {
 useState
} from 'react';
import {
 Label
} from 'shared/components/ui/label';
import {
 Input
} from 'shared/components/ui/input';
import {
 Button
} from 'shared/components/ui/button';
import {
 ChevronDown, ChevronUp, Plus, Trash2
} from 'lucide-react';
import {
    FlowDefinition, JsonOpcodeStep, StepKind, OutputMapping, TypedOutput
} from 'builders/builderOpcode/jsonMode/types';
import {
 buildV2Step
} from '../V2StepBuilder';
import {
 V2_STEP_TYPES
} from '../V2OpCodeBuilderContext';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from 'shared/components/ui/select';
import dispatchInlineStep from 'builders/destudio/opcode-editor/inline-steps/dispatchInlineStep';
import {
 buildOelContext
} from 'builders/destudio/primitives/oel';
import OutputMappingEditor from './OutputMappingEditor';

const FlowsEditor = ({
    flows,
    definitions,
    onChange,
    inputs = [],
}: {
    flows: FlowDefinition[];
    definitions: JsonOpcodeStep[];
    onChange: (flows: FlowDefinition[]) => void;
    inputs?: string[];
}) => {
    const [isOpen, setIsOpen] = useState(flows.length > 0);
    const [expandedFlowIndex, setExpandedFlowIndex] = useState<number | null>(null);
    const [expandedStepIndex, setExpandedStepIndex] = useState<number | null>(null);

    /**
     * Convert the OutputMapping (legacy V2 map shape `{ name: oel }`) into the
     * post-A-0008.03 TypedOutput[] shape that Server Joi enforces. Each entry
     * defaults to `type: 'string'`; richer typing can be edited later via JSON
     * mode or a future per-row editor.
     */
    const mapToTypedOutputs = (map: OutputMapping): TypedOutput[] => Object.entries(map || {})
        .filter(([name]) => !!name)
        .map(([name, oel]) => ({
            name,
            type: 'string' as const,
            // OutputMapping values are JsonValue (string|number|boolean|...).
            // Coerce to string so this satisfies the post-A-0008.03 typed
            // output shape; the OutputMappingEditor only emits strings in
            // practice, but TS doesn't know that from the wider type.
            oel_expression: typeof oel === 'string' ? oel : (oel == null ? '' : String(oel))
        }));

    const handleAddFlow = () => {
        const newFlow: FlowDefinition = {
            id: `flow_${flows.length + 1}`,
            steps: [],
            // Server Joi requires an array of typed outputs (A-0008.03 §3.5).
            output: []
        };
        onChange([...flows, newFlow]);
        setExpandedFlowIndex(flows.length);
        setIsOpen(true);
    };

    const handleRemoveFlow = (index: number) => {
        onChange(flows.filter((_, i) => i !== index));
        if (expandedFlowIndex === index) setExpandedFlowIndex(null);
    };

    const handleFlowIdChange = (index: number, id: string) => {
        onChange(flows.map((f, i) => i === index ? {
 ...f,
id
} : f));
    };

    const handleFlowOutputChange = (index: number, output: OutputMapping) => {
        // OutputMappingEditor still emits the legacy `{ name: oel }` map
        // shape. Normalize to TypedOutput[] so Server Joi accepts the save.
        const typed = mapToTypedOutputs(output);
        onChange(flows.map((f, i) => i === index ? {
            ...f,
            output: typed
        } : f));
    };

    const handleAddStepToFlow = (flowIndex: number) => {
        const flow = flows[flowIndex];
        const newStep = buildV2Step('llm', flow.steps);
        const updatedFlow = {
 ...flow,
steps: [...flow.steps, newStep]
};
        onChange(flows.map((f, i) => i === flowIndex ? updatedFlow : f));
        setExpandedStepIndex(flow.steps.length);
    };

    const handleRemoveStepFromFlow = (flowIndex: number, stepIndex: number) => {
        const flow = flows[flowIndex];
        const updatedFlow = {
            ...flow,
            steps: flow.steps.filter((_, i) => i !== stepIndex)
        };
        onChange(flows.map((f, i) => i === flowIndex ? updatedFlow : f));
    };

    const handleStepChange = (flowIndex: number, stepIndex: number, step: JsonOpcodeStep) => {
        const flow = flows[flowIndex];
        const updatedSteps = flow.steps.map((s, i) => i === stepIndex ? step : s);
        onChange(flows.map((f, i) => i === flowIndex ? {
 ...f,
steps: updatedSteps
} : f));
    };

    const handleStepKindChange = (flowIndex: number, stepIndex: number, kind: StepKind) => {
        const flow = flows[flowIndex];
        const existing = flow.steps[stepIndex];
        const newStep = buildV2Step(kind, flow.steps);
        const updatedSteps = flow.steps.map((s, i) =>
            i === stepIndex ? {
 ...newStep,
id: existing.id
} : s
        );
        onChange(flows.map((f, i) => i === flowIndex ? {
 ...f,
steps: updatedSteps
} : f));
    };

    return (
        <div className="border rounded-md">
            <Button
                type="button"
                variant="ghost"
                size="sm"
                className="w-full flex justify-between items-center px-3 py-2"
                onClick={() => setIsOpen(!isOpen)}
            >
                <span className="text-sm font-medium">
                    Flows ({flows.length})
                </span>
                {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>

            {isOpen && (
                <div className="p-3 space-y-3 border-t">
                    <p className="text-xs text-muted-foreground">
                        Named sub-workflows that can be referenced by Subflow and Choose steps.
                    </p>

                    {flows.map((flow, flowIndex) => (
                        // Key by index — flow.id is editable; keying on it
                        // would remount the row on every keystroke and steal
                        // input focus from the Flow ID field.
                        // eslint-disable-next-line react/no-array-index-key
                        <div key={flowIndex} className="border rounded-md">
                            <div
                                className="flex items-center gap-2 p-2 cursor-pointer hover:bg-accent/50"
                                onClick={() => {
                                    setExpandedFlowIndex(expandedFlowIndex === flowIndex ? null : flowIndex);
                                    setExpandedStepIndex(null);
                                }}
                            >
                                <span className="text-sm font-mono font-semibold flex-1">{flow.id}</span>
                                <span className="text-xs text-muted-foreground">{flow.steps.length} steps</span>
                                <Button
                                    type="button"
                                    size="icon"
                                    variant="ghost"
                                    onClick={(e) => { e.stopPropagation(); handleRemoveFlow(flowIndex); }}
                                >
                                    <Trash2 className="h-4 w-4 text-destructive" />
                                </Button>
                                {expandedFlowIndex === flowIndex ? (
                                    <ChevronUp className="h-4 w-4" />
                                ) : (
                                    <ChevronDown className="h-4 w-4" />
                                )}
                            </div>

                            {expandedFlowIndex === flowIndex && (
                                <div className="p-3 space-y-3 border-t">
                                    {/* Flow ID */}
                                    <div className="space-y-1">
                                        <Label className="text-xs">Flow ID</Label>
                                        <Input
                                            value={flow.id}
                                            onChange={(e) => handleFlowIdChange(flowIndex, e.target.value)}
                                            placeholder="flow_name"
                                            className="font-mono text-sm"
                                        />
                                    </div>

                                    {/* Flow Steps */}
                                    <div className="space-y-2">
                                        <Label className="text-xs font-semibold">Steps</Label>
                                        {flow.steps.map((step, stepIndex) => (
                                            // Same reason — step.id is editable.
                                            // eslint-disable-next-line react/no-array-index-key
                                            <div key={stepIndex} className="border rounded-md">
                                                <div
                                                    className="flex items-center gap-2 p-2 cursor-pointer hover:bg-accent/50"
                                                    onClick={() => setExpandedStepIndex(
                                                        expandedStepIndex === stepIndex ? null : stepIndex
                                                    )}
                                                >
                                                    <span className="text-xs font-mono">{step.id}</span>
                                                    <span className="text-xs text-muted-foreground">{step.kind}</span>
                                                    <div className="flex-1" />
                                                    <Button
                                                        type="button"
                                                        size="icon"
                                                        variant="ghost"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleRemoveStepFromFlow(flowIndex, stepIndex);
                                                        }}
                                                    >
                                                        <Trash2 className="h-3 w-3 text-destructive" />
                                                    </Button>
                                                </div>

                                                {expandedStepIndex === stepIndex && (
                                                    <div className="p-3 space-y-2 border-t">
                                                        <Select
                                                            value={step.kind}
                                                            onValueChange={(val) =>
                                                                handleStepKindChange(flowIndex, stepIndex, val as StepKind)
                                                            }
                                                        >
                                                            <SelectTrigger>
                                                                <SelectValue />
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                {V2_STEP_TYPES.map((t) => (
                                                                    <SelectItem key={t.name} value={t.name}>
                                                                        {t.label}
                                                                    </SelectItem>
                                                                ))}
                                                            </SelectContent>
                                                        </Select>

                                                        {/* Render flow-internal steps with the destudio
                                                            inline editors so they look identical to top-level
                                                            opcode steps. ctx is flow-local: previousSteps =
                                                            steps[0..stepIndex) from THIS flow. */}
                                                        {dispatchInlineStep({
                                                            step,
                                                            onPatchStep: (updated: JsonOpcodeStep) =>
                                                                handleStepChange(flowIndex, stepIndex, updated),
                                                            definitions,
                                                            flows,
                                                            oelContext: buildOelContext({
                                                                inputs: inputs.map((n) => ({
 name: n,
type: 'string'
})),
                                                                steps: flow.steps,
                                                                currentStepIdx: stepIndex,
                                                                definitions,
                                                                flows,
                                                                loopVars: step.for ? [{
 name: step.for.item
}] : []
                                                            })
                                                        })}
                                                    </div>
                                                )}
                                            </div>
                                        ))}

                                        <Button
                                            type="button"
                                            size="sm"
                                            variant="outline"
                                            onClick={() => handleAddStepToFlow(flowIndex)}
                                            className="w-full"
                                        >
                                            <Plus className="h-3 w-3 mr-2" />
                                            Add Step
                                        </Button>
                                    </div>

                                    {/* Flow Output */}
                                    {/* A-0008.03: flow.output is TypedOutput[] | LegacyOutputMapping; the
                                        legacy editor only knows the map shape. PR-3 Stage 3 replaces this
                                        with a TypedOutput list editor. For now, coerce to a map. */}
                                    <OutputMappingEditor
                                        output={Array.isArray(flow.output)
                                            ? Object.fromEntries(flow.output.map(o => [o.name, o.oel_expression]))
                                            : flow.output}
                                        onChange={(output) => handleFlowOutputChange(flowIndex, output)}
                                        oelContext={{
                                            inputs,
                                            previousStepIds: flow.steps.map(s => s.id),
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                    ))}

                    <Button type="button" size="sm" variant="outline" onClick={handleAddFlow} className="w-full">
                        <Plus className="h-4 w-4 mr-2" />
                        Add Flow
                    </Button>
                </div>
            )}
        </div>
    );
};

export default FlowsEditor;
